import React, { useEffect, useRef } from 'react';
import { Spin, Empty, Image } from 'antd';
import { RobotOutlined, UserOutlined, LoadingOutlined } from '@ant-design/icons';
import type { Message, GeneratedImage } from '../../../types/aiGenerate';
import { useTranslation } from '../../../hooks/useTranslation';
import './index.css';

interface ChatAreaProps {
  messages: Message[];
  loading: boolean;
  showCompare: boolean;
}

// 格式化时间
const formatTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const GeneratingImage: React.FC<{ image: GeneratedImage }> = ({ image }) => {
  const { t } = useTranslation();
  if (image.status === 'generating' || image.status === 'pending') {
    return (
      <div className="generating-image">
        <div className="generating-image-loading">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
          />
          {image.progress !== undefined && image.progress > 0 && (
            <div className="generating-progress">{image.progress}%</div>
          )}
        </div>
      </div>
    );
  }

  if (image.status === 'failed') {
    return (
      <div className="generating-image failed">
        <span>{t('aiGenerate.generateFailed')}</span>
      </div>
    );
  }

  return (
    <div className="generated-image-wrapper">
      <Image
        src={image.url}
        alt={t('aiGenerate.generatedImageAlt')}
        className="generated-image"
        placeholder={
          <div className="image-placeholder">
            <Spin />
          </div>
        }
      />
      {(image.width || image.height || image.requestedQuality) && (
        <div className="generated-image-meta">
          {image.width && image.height ? `${image.width}×${image.height}` : image.requestedSize}
          {image.requestedQuality ? ` · ${image.requestedQuality}` : ''}
        </div>
      )}
    </div>
  );
};

const MessageItem: React.FC<{
  message: Message;
  showCompare: boolean;
  inputImages?: string[];
}> = ({
  message,
  showCompare,
  inputImages,
}) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-avatar">
        {isUser ? (
          <div className="avatar user-avatar">
            <UserOutlined />
          </div>
        ) : (
          <div className="avatar assistant-avatar">
            <RobotOutlined />
          </div>
        )}
      </div>
      <div className="message-content-wrapper">
        <div className="message-header">
          <span className="message-role">{isUser ? '用户' : '图床AI'}</span>
        </div>
        <div className="message-body">
          {/* 文本内容 */}
          {message.content && (
            <div className="message-text">{message.content}</div>
          )}

          {/* 用户输入的图片 */}
          {isUser && message.images && message.images.length > 0 && (
            <div className="message-input-images">
              <Image.PreviewGroup>
                {message.images.map((url, index) => (
                  <Image
                    key={index}
                    src={url}
                    alt={`输入图片 ${index + 1}`}
                    className="input-image-thumbnail"
                    width={120}
                    height={120}
                    style={{ objectFit: 'cover' }}
                  />
                ))}
              </Image.PreviewGroup>
            </div>
          )}

          {/* 生成参数显示 */}
          {isUser && message.params && (
            <div className="message-params">
              <span>模型: {message.params.model === 'gpt-image-2' ? 'GPT Image 2' : message.params.model}</span>
              <span>数量: {message.params.count}张</span>
              <span>尺寸: {message.params.size || message.params.aspectRatio || '-'}</span>
              {message.params.quality && <span>质量: {message.params.quality}</span>}
              {!message.params.size && message.params.resolution && <span>分辨率: {message.params.resolution}</span>}
            </div>
          )}

          {/* AI 生成的图片 */}
          {!isUser && message.generatedImages && message.generatedImages.length > 0 && (
            <div className="message-generated-images">
              {(() => {
                // 按原图分组
                const groupedImages = new Map<string | undefined, GeneratedImage[]>();
                message.generatedImages.forEach((img) => {
                  const key = img.sourceImageUrl;
                  if (!groupedImages.has(key)) {
                    groupedImages.set(key, []);
                  }
                  groupedImages.get(key)!.push(img);
                });

                // 如果只有一组且没有原图，直接显示
                if (groupedImages.size === 1 && !groupedImages.has(inputImages?.[0])) {
                  const images = Array.from(groupedImages.values())[0];
                  return (
                    <div className={showCompare && inputImages && inputImages.length > 0 ? 'compare-group' : ''}>
                      {showCompare && inputImages && inputImages.length > 0 && (
                        <div className="compare-item">
                          <div className="compare-original">
                            <div className="compare-label">{t('aiGenerate.compareOriginalLabel')}</div>
                            <Image src={inputImages[0]} alt={t('aiGenerate.originalImageAlt')} className="compare-image" />
                          </div>
                          <div className="compare-generated">
                            <div className="compare-label">{t('aiGenerate.compareGeneratedLabel')}</div>
                            <div className="generated-images-grid">
                              {images.map((img) => (
                                <GeneratingImage key={img.id} image={img} />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {(!showCompare || !inputImages || inputImages.length === 0) && (
                        <div className="generated-images-grid">
                          {images.map((img) => (
                            <GeneratingImage key={img.id} image={img} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // 多张原图，按原图分组显示
                return (
                  <div className="batch-results">
                    {inputImages && inputImages.map((sourceUrl, idx) => {
                      const images = groupedImages.get(sourceUrl) || [];
                      if (images.length === 0) return null;
                      
                      return (
                        <div key={idx} className={showCompare ? 'compare-item' : 'batch-item'}>
                          {showCompare && (
                            <div className="compare-original">
                              <div className="compare-label">原图 {idx + 1}</div>
                              <Image src={sourceUrl} alt={`原图 ${idx + 1}`} className="compare-image" />
                            </div>
                          )}
                          <div className={showCompare ? 'compare-generated' : ''}>
                            {showCompare && <div className="compare-label">生成图</div>}
                            {!showCompare && inputImages.length > 1 && (
                              <div className="batch-label">图片 {idx + 1} 的生成结果</div>
                            )}
                            <div className="generated-images-grid">
                              {images.map((img) => (
                                <GeneratingImage key={img.id} image={img} />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* 没有关联原图的生成结果（纯文生图） */}
                    {groupedImages.has(undefined) && (
                      <div className="generated-images-grid">
                        {groupedImages.get(undefined)!.map((img) => (
                          <GeneratingImage key={img.id} image={img} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        <div className="message-time">{formatTime(message.createdAt)}</div>
      </div>
    </div>
  );
};

const ChatArea: React.FC<ChatAreaProps> = ({ messages, loading, showCompare }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  if (loading) {
    return (
      <div className="chat-area-loading">
        <Spin size="large" />
        <p>加载中...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="chat-area-empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="empty-description">
              <h3>开始 AI 生图</h3>
              <p>上传图片或输入提示词，AI 将为您生成精美图片</p>
              <ul>
                <li>支持上传参考图片进行风格转换</li>
                <li>可以从右侧选择场景模板快速开始</li>
                <li>支持调整生成数量、尺寸和分辨率</li>
              </ul>
            </div>
          }
        />
      </div>
    );
  }

  // 找到 AI 消息对应的用户输入图片
  const getInputImagesForAiMessage = (index: number): string[] | undefined => {
    // 往前查找最近的用户消息
    for (let i = index - 1; i >= 0; i--) {
      if (messages[i].role === 'user' && messages[i].images && messages[i].images!.length > 0) {
        return messages[i].images;
      }
    }
    return undefined;
  };

  return (
    <div className="chat-area" ref={containerRef}>
      <div className="chat-messages">
        {messages.map((message, index) => (
          <MessageItem 
            key={message.id} 
            message={message} 
            showCompare={showCompare}
            inputImages={message.role === 'assistant' ? getInputImagesForAiMessage(index) : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatArea;
